// Historic-months navigator for the Bills page ("By Month" view).
// Desktop keeps the horizontally-scrollable chip strip so the visual
// hierarchy is preserved when users have room. On mobile, only ~1.5
// chips fit inside a 390px card and the rest is hidden behind an
// invisible horizontal scroll — confusing enough that Feb 2026 users
// asked us to redesign it. Below `sm` we render a single dropdown
// instead. It gives ALL months in one tap, no scroll gestures required.
import { formatMoney } from "@/lib/api";

export function MonthStripNav({ chips, selectedKey, onSelect }) {
  return (
    <div className="card-soft !p-3" data-testid="bills-month-strip">
      {/* Desktop — chip strip */}
      <div className="hidden sm:flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        <button
          type="button"
          onClick={() => onSelect(null)}
          className={`shrink-0 rounded-2xl px-3 py-2 text-xs transition-colors border ring-1 ring-[#4A6148]/40 ${
            selectedKey === null
              ? "bg-[#4A6148] text-white border-[#4A6148]"
              : "bg-[#FFFBEA] text-[#8C5D30] border-[#F3E1CF] hover:bg-[#FFF6D0]"
          }`}
          data-testid="bills-month-chip-default"
        >
          <div className="font-heading text-sm whitespace-nowrap">Current Month Bills</div>
        </button>
        {chips.map((c) => {
          const active = selectedKey === c.key;
          const bg = active
            ? "bg-[#4A6148] text-white border-[#4A6148]"
            : "bg-white text-[#59605C] border-[#EBE6E0] hover:bg-[#F2EFEB]";
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => onSelect(c.key)}
              className={`shrink-0 rounded-2xl px-3 py-2 text-xs border transition-colors min-w-[92px] ${bg}`}
              title={c.count === 0 ? "No bills" : `${c.count} bill${c.count === 1 ? "" : "s"} · Unpaid ${formatMoney(c.unpaidTotal)} · Paid ${formatMoney(c.paidTotal)}`}
              data-testid={`bills-month-chip-${c.key}`}
            >
              <div className="font-semibold uppercase tracking-widest text-[10px] whitespace-nowrap">
                {`${-c.offset} mo ago`}
              </div>
              <div className="mt-0.5 font-heading text-sm whitespace-nowrap">{c.label}</div>
              <div className={`mt-0.5 text-[11px] ${active ? "text-white/80" : "text-[#8A918D]"} whitespace-nowrap`}>
                {c.count === 0 ? "—" : formatMoney(c.total)}
              </div>
            </button>
          );
        })}
      </div>

      {/* Mobile — dropdown. Wraps the same options so the select's own
          keyboard/tap behaviour handles all interaction. */}
      <div className="sm:hidden">
        <label className="block text-[10px] font-semibold uppercase tracking-widest text-[#8A918D] mb-1.5">
          Viewing
        </label>
        <select
          value={selectedKey ?? "__current__"}
          onChange={(e) => onSelect(e.target.value === "__current__" ? null : e.target.value)}
          className="input-soft w-full text-sm py-2"
          data-testid="bills-month-select"
        >
          <option value="__current__">Current Month Bills</option>
          {chips.map((c) => (
            <option key={c.key} value={c.key}>
              {`${-c.offset} mo ago · ${c.label}${c.count === 0 ? " · —" : ` · ${formatMoney(c.total)}`}`}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
